// Fallback vide = chemins relatifs, résolus par le navigateur vers l'origine courante.
// En dev local, VITE_API_URL est défini dans .env.development (http://localhost:4000).
const API_BASE_URL: string =
  (import.meta.env["VITE_API_URL"] as string | undefined) ?? "";

if (import.meta.env.PROD && API_BASE_URL.includes("localhost")) {
  console.error(
    "[IllustraClass] API_BASE_URL contient 'localhost' en production — vérifier VITE_API_URL",
  );
}

type ApiOptions = RequestInit & {
  skipAuth?: boolean;
};

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

async function refreshAccessToken(): Promise<string | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });

    if (!response.ok) return null;

    const data = (await response.json()) as { accessToken: string };
    accessToken = data.accessToken;
    return accessToken;
  } catch {
    return null;
  }
}

async function ensureToken(): Promise<string | null> {
  if (accessToken) return accessToken;

  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

export async function api<T = unknown>(
  path: string,
  options: ApiOptions = {},
): Promise<T> {
  const { skipAuth = false, headers: customHeaders, ...fetchOptions } = options;

  const headers = new Headers(customHeaders);

  if (!skipAuth) {
    const token = await ensureToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  if (!headers.has("Content-Type") && fetchOptions.body) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...fetchOptions,
    headers,
    credentials: "include",
  });

  if (response.status === 401 && !skipAuth) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers.set("Authorization", `Bearer ${newToken}`);
      const retryResponse = await fetch(`${API_BASE_URL}${path}`, {
        ...fetchOptions,
        headers,
        credentials: "include",
      });

      if (!retryResponse.ok) {
        throw await parseError(retryResponse);
      }

      return retryResponse.json() as Promise<T>;
    }

    accessToken = null;
    throw await parseError(response);
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("json")) {
    return response.json() as Promise<T>;
  }

  if (contentType.includes("text/html")) {
    throw new ApiError(
      502,
      "Réponse inattendue du serveur (HTML au lieu de JSON). Vérifiez la configuration du reverse proxy.",
    );
  }

  return undefined as T;
}

type ProblemDetail = {
  type: string;
  title: string;
  status: number;
  detail: string;
};

export class ApiError extends Error {
  readonly status: number;
  readonly problem?: ProblemDetail;

  constructor(status: number, message: string, problem?: ProblemDetail) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as ProblemDetail;
    return new ApiError(response.status, body.detail || body.title, body);
  } catch {
    return new ApiError(response.status, response.statusText);
  }
}
